/**
 * Вебманіфест модуля (module-contract.md, розд. 5.8; AG-51): лише дані й ліниві завантажувачі,
 * без React і стилів. Оболонка збирає вебманіфести всіх модулів під час збірки
 * (`import.meta.glob`), тож пункти навігації, слоти й команди відомі до завантаження чанків
 * модуля, а тест CI перевіряє їх без браузера. Шлях `@clientline/contracts/web-module` — без zod.
 */

import type { TimelineRow } from './timeline.ts';

/** Підпис кількома мовами; `i18n:<ключ>` — ключ каталогу модуля. */
export type WebText = { uk: string; en?: string } | `i18n:${string}`;

/** Умова внеску: поведінка чи сутність запису, вибір у списку, право. */
export interface SlotWhen {
  /** Ключ сутності (канонічний) або модуль-власник сутності (`platform:<id>`). */
  entity?: string;
  owner?: string;
  behavior?: string;
  selection?: 'none' | 'one' | 'many';
}

/** Лінивий завантажувач компонента внеску: `default` — компонент React модуля. */
export type LazyComponent = () => Promise<{ default: unknown }>;

/** Запис, який оболонка передає внеску картки (`record.panels`) і команді області `record`. */
export interface SlotRecord {
  entity: string;
  id: string;
  version: number;
  /**
   * Масив — список значень поля-списку (`SlotListItem`) чи ключі мультисписку (рядки); об'єкт —
   * гроші (`SlotMoney`, FLD-05).
   */
  values: Readonly<
    Record<
      string,
      string | number | boolean | null | SlotMoney | readonly (SlotListItem | string)[]
    >
  >;
}

/** Значення грошового поля (FLD-05): сума рядком десяткового числа і код валюти ISO 4217. */
export interface SlotMoney {
  readonly amount: string;
  readonly currency: string;
}

/** Значення поля-списку (телефон, пошта, ідентифікатор; Б3): основне — перше. */
export interface SlotListItem {
  value: string;
  type?: string | undefined;
  ext?: string | undefined;
}

/** Пропси компонента внеску `record.panels`. `t` — підпис з каталогу модуля за ключем. */
export interface RecordPanelProps {
  record: SlotRecord;
  t: (key: string) => string;
}

/**
 * Пропси рендерера рядка лінії (слот `record.timeline`, timeline.md, розд. 5.1): `record` — запис,
 * чию лінію показано; `compact` — згорнутий вигляд ваги `system`; `t` — підпис з каталогу модуля.
 */
export interface TimelineRendererProps {
  row: TimelineRow;
  record: SlotRecord;
  compact: boolean;
  t: (key: string) => string;
}

/**
 * Внесок у слот `record.timeline`: `id` — ключ типу, який модуль оголосив у серверному маніфесті
 * (`timeline.types`, звірка маніфестів — тест CI). `load` лінивий, як у `record.panels`: оболонка
 * вантажить чанк модуля один раз на всі його типи на сторінці, а доки він не прийшов, показує
 * рядок узагальнено («Подія · джерело · час») тієї самої висоти. Умови й права внеску немає: що
 * видно, вирішує сервер у `TimelineRow`.
 */
export interface TimelineRendererContribution {
  id: string;
  load: LazyComponent;
}

/** Що отримує обробник команди: запис (для області `record`) і запит до API з ключем дії. */
export interface CommandContext {
  record: SlotRecord | null;
  /** POST/PATCH до `/api/v1/...` з `Idempotency-Key` дії людини; помилка — `ApiProblem`. */
  send: (method: 'POST' | 'PATCH', url: string, body: unknown) => Promise<unknown>;
  t: (key: string) => string;
}

export type CommandRun = (context: CommandContext) => Promise<void>;

export interface NavItem {
  id: string;
  label: WebText;
  route: string;
  /** Право, без якого пункт не показується. */
  permission: string;
  icon?: string;
  /**
   * Місце в одному списку з пунктами реєстру (`/meta.nav`, UX-02): менше — вище. Немає — після
   * всіх пунктів з місцем, у порядку модулів.
   */
  order?: number;
}

export interface WebRoute {
  id: string;
  path: string;
  load: LazyComponent;
}

export interface SlotContribution {
  id: string;
  label?: WebText;
  when?: SlotWhen;
  /** Право, без якого внесок не показується (інтерфейс не вгадує прав, але й не просить зайвого). */
  permission?: string;
  load: LazyComponent;
}

export interface WebCommand {
  /** `<модуль>.<дія>`, унікальний. */
  id: string;
  label: WebText;
  keywords?: WebText[];
  scope: 'record' | 'list' | 'palette' | `screen:${string}`;
  when?: SlotWhen;
  /** Право модуля або дія над записом (`allowedActions` картки). */
  permission: string | { record: 'update' };
  /** Фізична клавіша (`event.code`); `mods: ['ctrl']` → ⌘ на macOS. */
  keys?: { code: string; mods?: readonly ('ctrl' | 'shift' | 'alt')[] };
  run: () => Promise<{ default: CommandRun }>;
}

/**
 * Групи розділу «Адміністрування» (ADM-801): невелике дерево за завданнями адміністратора.
 * Набір і порядок задає платформа; модуль лише обирає групу для своєї сторінки.
 */
export const ADMIN_GROUPS = [
  'company',
  'access',
  'model',
  'processes',
  'data',
  'integrations',
] as const;

export type AdminGroup = (typeof ADMIN_GROUPS)[number];

/**
 * Сторінка модуля в розділі «Адміністрування» (`/admin/<id>`; ADM-801, layout.md, розд. 6).
 * Право обов'язкове: сторінки без права немає ні в меню, ні в пошуку, а її код не вантажиться.
 * Право в інтерфейсі лише ховає сторінку; дію перевіряє сервер.
 */
export interface AdminPageContribution {
  /** `<модуль>.<сторінка>` — це й сегмент адреси. */
  id: string;
  label: WebText;
  group: AdminGroup;
  permission: string;
  /** Одне речення під назвою на головній адмінки. */
  description?: WebText;
  /** Ключові слова й синоніми для пошуку («курс», «гроші» → «Валюти»). */
  keywords?: readonly WebText[];
  load: LazyComponent;
}

/** Слоти оболонки з внеском `SlotContribution` (module-contract.md, таблиця слотів). */
export type SlotName =
  | 'record.panels'
  | 'record.tabs'
  | 'record.actions'
  | 'list.actions'
  | 'quickCreate'
  | 'dashboard.widgets';

export interface WebModuleManifest {
  id: string;
  nav?: readonly NavItem[];
  routes?: readonly WebRoute[];
  slots?: Partial<Record<SlotName, readonly SlotContribution[]>> & {
    'record.timeline'?: readonly TimelineRendererContribution[];
  };
  commands?: readonly WebCommand[];
  adminPages?: readonly AdminPageContribution[];
}

/**
 * Підписи `i18n:<ключ>` беруться з каталогу модуля за домовленістю шляху —
 * `modules/<id>/i18n/uk.ts` (`export default { ключ: 'текст' }`): оболонка збирає каталоги
 * разом із вебманіфестами, бо підписи навігації потрібні першому екрану.
 */
export const WEB_I18N_PATH = 'i18n/uk.ts';

const MODULE_ID = /^[a-z][a-z0-9_]{0,39}$/;
/** Ключ типу лінії (`TIMELINE_TYPE_KEY` у `./timeline.ts`; тут без zod-модуля). */
const TIMELINE_TYPE_ID = /^[a-z][a-z0-9_]{0,39}\.[a-z][a-z0-9_]{0,39}$/;
/** Сегмент адреси `/admin/<id>`: `<модуль>.<сторінка>`, без `/` і символів, які треба кодувати. */
const ADMIN_PAGE_ID = /^[a-z][a-z0-9_]{0,39}\.[a-z][a-z0-9_-]{0,39}$/;

/**
 * Перевіряє форму вебманіфесту: ідентифікатори внесків і команд мають префікс модуля
 * (`<модуль>.`), щоб внески різних модулів не конфліктували.
 */
export function defineWebModule<const T extends WebModuleManifest>(manifest: T): T {
  if (!MODULE_ID.test(manifest.id)) throw new Error(`Вебманіфест: некоректний id ${manifest.id}`);
  const prefix = `${manifest.id}.`;
  const ids = [
    ...(manifest.nav ?? []).map((n) => n.id),
    ...(manifest.routes ?? []).map((r) => r.id),
    ...Object.values(manifest.slots ?? {}).flatMap((items) => (items ?? []).map((c) => c.id)),
    ...(manifest.commands ?? []).map((c) => c.id),
    ...(manifest.adminPages ?? []).map((p) => p.id),
  ];
  for (const id of ids) {
    if (!id.startsWith(prefix)) {
      throw new Error(`Вебманіфест ${manifest.id}: внесок ${id} має починатися з «${prefix}»`);
    }
  }
  const renderers = new Set<string>();
  for (const renderer of manifest.slots?.['record.timeline'] ?? []) {
    if (!TIMELINE_TYPE_ID.test(renderer.id)) {
      throw new Error(`Вебманіфест ${manifest.id}: рендерер ${renderer.id} — не ключ типу лінії`);
    }
    if (renderers.has(renderer.id)) {
      throw new Error(`Вебманіфест ${manifest.id}: рендерер ${renderer.id} оголошено двічі`);
    }
    renderers.add(renderer.id);
  }
  for (const page of manifest.adminPages ?? []) {
    if (!(ADMIN_GROUPS as readonly string[]).includes(page.group)) {
      throw new Error(
        `Вебманіфест ${manifest.id}: сторінка ${page.id} — невідома група ${page.group}`,
      );
    }
    if (!page.permission) {
      throw new Error(`Вебманіфест ${manifest.id}: сторінка ${page.id} без права`);
    }
    if (!ADMIN_PAGE_ID.test(page.id)) {
      throw new Error(`Вебманіфест ${manifest.id}: некоректний id сторінки ${page.id}`);
    }
  }
  return manifest;
}
